/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { Row, Col, Form, Label, Input, Button } from 'reactstrap';
import DOMPurify from 'dompurify';
import InlineQuillEditor from '../../../../Components/Common/InlineQuillEditor';

interface DescriptionData {
  language: string;
  shortName: string;
  shortDescription: string;
  marketingName: string;
  description: string;
}

const DescriptionTab: React.FC = () => {
  const [formData, setFormData] = useState<DescriptionData>({
    language: 'English',
    shortName: '',
    shortDescription: '',
    marketingName: '',
    description: '',
  });

  // Inline editing state for each field
  const [editingShortName, setEditingShortName] = useState(false);
  const [editingShortDescription, setEditingShortDescription] = useState(false);
  const [editingMarketingName, setEditingMarketingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);

  // Only one editor open at a time
  const handleOpenEditor = (
    field: 'shortName' | 'shortDescription' | 'marketingName' | 'description'
  ) => {
    setEditingShortName(field === 'shortName');
    setEditingShortDescription(field === 'shortDescription');
    setEditingMarketingName(field === 'marketingName');
    setEditingDescription(field === 'description');
  };

  const handleInputChange =
    (field: keyof DescriptionData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData({ ...formData, [field]: e.target.value });
    };

  // Function to safely render HTML content
  const createMarkup = (html: string) => {
    return {
      __html: DOMPurify.sanitize(html, {
        ADD_ATTR: ['class', 'style'],
        ADD_TAGS: ['span'],
      }),
    };
  };

  return (
    <>
      <Form>
        <Row className="mb-3">
          <Col sm={2}>
            <div className="mb-3">
              <Label className="form-label mb-0">Language</Label>
              <Input
                type="select"
                className="form-control form-control-sm"
                value={formData.language}
                onChange={handleInputChange('language')}
              >
                <option>English</option>
              </Input>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col sm={6}>
            <div className="mb-3">
              <Label className="form-label mb-0">Short Name</Label>
              <div className="d-flex align-items-start">
                {!editingShortName ? (
                  <>
                    <div
                      className="form-control form-control-sm overflow-auto"
                      style={{
                        height: '31px',
                        maxHeight: '31px',
                        overflowY: 'auto',
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={createMarkup(
                          formData.shortName
                        )}
                      />
                    </div>
                    <Button
                      color="light"
                      size="sm"
                      className="ms-2"
                      style={{ minWidth: '60px' }}
                      onClick={() => handleOpenEditor('shortName')}
                    >
                      Edit
                    </Button>
                  </>
                ) : (
                  <InlineQuillEditor
                    value={formData.shortName}
                    minHeight={31}
                    onSave={(val: string) => {
                      setFormData((prev) => ({ ...prev, shortName: val }));
                      setEditingShortName(false);
                    }}
                    onCancel={() => setEditingShortName(false)}
                  />
                )}
              </div>
            </div>
          </Col>

          <Col sm={6}>
            <div className="mb-3">
              <Label className="form-label mb-0">Marketing Name</Label>
              <div className="d-flex align-items-start">
                {!editingMarketingName ? (
                  <>
                    <div
                      className="form-control form-control-sm overflow-auto"
                      style={{
                        height: '31px',
                        maxHeight: '31px',
                        overflowY: 'auto',
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={createMarkup(
                          formData.marketingName
                        )}
                      />
                    </div>
                    <Button
                      color="light"
                      size="sm"
                      className="ms-2"
                      style={{ minWidth: '60px' }}
                      onClick={() => handleOpenEditor('marketingName')}
                    >
                      Edit
                    </Button>
                  </>
                ) : (
                  <InlineQuillEditor
                    value={formData.marketingName}
                    minHeight={31}
                    onSave={(val: string) => {
                      setFormData((prev) => ({ ...prev, marketingName: val }));
                      setEditingMarketingName(false);
                    }}
                    onCancel={() => setEditingMarketingName(false)}
                  />
                )}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col sm={12}>
            <div className="mb-3">
              <Label className="form-label mb-0">Short Description</Label>
              <div className="d-flex align-items-start">
                {!editingShortDescription ? (
                  <>
                    <div
                      className="form-control form-control-sm overflow-auto"
                      style={{
                        height: '80px',
                        maxHeight: '80px',
                        overflowY: 'auto',
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={createMarkup(
                          formData.shortDescription
                        )}
                      />
                    </div>
                    <Button
                      color="light"
                      size="sm"
                      className="ms-2"
                      style={{ minWidth: '60px' }}
                      onClick={() => handleOpenEditor('shortDescription')}
                    >
                      Edit
                    </Button>
                  </>
                ) : (
                  <InlineQuillEditor
                    value={formData.shortDescription}
                    minHeight={80}
                    onSave={(val: string) => {
                      setFormData((prev) => ({ ...prev, shortDescription: val }));
                      setEditingShortDescription(false);
                    }}
                    onCancel={() => setEditingShortDescription(false)}
                  />
                )}
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col sm={12}>
            <div className="mb-3">
              <Label className="form-label mb-0">Description</Label>
              <div className="d-flex align-items-start">
                {!editingDescription ? (
                  <>
                    <div
                      className="form-control form-control-sm overflow-auto"
                      style={{
                        height: '100px',
                        maxHeight: '100px',
                        overflowY: 'auto',
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={createMarkup(
                          formData.description
                        )}
                      />
                    </div>
                    <Button
                      color="light"
                      size="sm"
                      className="ms-2"
                      style={{ minWidth: '60px' }}
                      onClick={() => handleOpenEditor('description')}
                    >
                      Edit
                    </Button>
                  </>
                ) : (
                  <InlineQuillEditor
                    value={formData.description}
                    minHeight={100}
                    onSave={(val: string) => {
                      setFormData((prev) => ({ ...prev, description: val }));
                      setEditingDescription(false);
                    }}
                    onCancel={() => setEditingDescription(false)}
                  />
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default DescriptionTab;
